/**
 * The Object Dossier — the storefront's signature interaction (Design Bible
 * 19.1).
 *
 * The Slab Frame is the signature element for a *thing*; the Evidence Spine
 * (18.1) is the signature element for a *decision about a thing*. The Dossier
 * is the signature element for the one question a collector actually has to
 * answer: **which of these facts are true of the release, and which are true of
 * this exact object in front of me?**
 *
 * That distinction is the three-identity-level rule in MASTER_SPEC 0.5.2, and
 * on a generic ecommerce card it is invisible — the release title and the grade
 * sit in the same block of grey text, so a buyer cannot tell that one of them
 * describes 50,000 cartridges and the other describes one. The Dossier makes it
 * structural: two named regions, always both present, never merged, never
 * behind a disclosure.
 *
 * Rules:
 *
 * - **Two regions, always both.** A release with no description renders an
 *   explicit statement, not an empty column — the same rule as 18.1's three
 *   nodes and 13.3's three capture facts.
 * - **It collapses semantically, not visually.** Below `desk` the grid becomes
 *   one column and the two regions stack *in reading order with their headings
 *   intact*. Nothing is hidden, truncated, or moved behind a tab, so a screen
 *   reader and a 320px viewport get the same document as a 27" monitor.
 * - **Nothing in it moves.** There is no reveal and no stagger, so reduced
 *   motion removes nothing (8.4).
 * - **The exact-copy region is the one that travels.** `ItemIdentity` renders
 *   its irreducible core — SKU, grader, grade, cert — and is used unchanged in
 *   the cart, in the checkout summary, and on the confirmation, so the object
 *   the collector inspected is provably the object they are paying for.
 */

import type { ReactNode } from "react";
import type { PublicItem } from "../lib/publicContract.js";
import { availabilityLabel } from "../lib/labels.js";
import { DataPlate, Identifier, Money, NotPublished, PlateGrid, Quoted, StatusChip } from "./primitives.js";

export function ObjectDossier({ item }: { item: PublicItem }): ReactNode {
  const availability = availabilityLabel(item.availability, item.checkout_eligible);
  return (
    <div className="dossier">
      <section aria-labelledby="dossier-release" className="dossier-region">
        <h2 className="t-display-xs" id="dossier-release">
          The release
        </h2>
        <p className="t-body-s t-secondary dossier-scope">
          Approved catalogue facts. These are the same for every copy of this release.
        </p>
        <PlateGrid>
          <DataPlate label="Platform">
            <span className="t-body-m">{item.platform}</span>
          </DataPlate>
          <DataPlate label="Region">
            <span className="t-body-m">{item.region}</span>
          </DataPlate>
          <DataPlate label="Publisher">
            {item.publisher === undefined ? (
              <NotPublished what="no publisher on the approved record" />
            ) : (
              <span className="t-body-m">{item.publisher}</span>
            )}
          </DataPlate>
          <DataPlate label="Developer">
            {item.developer === undefined ? (
              <NotPublished what="no developer on the approved record" />
            ) : (
              <span className="t-body-m">{item.developer}</span>
            )}
          </DataPlate>
          <DataPlate label="Edition or variant">
            <span className="t-body-m">{item.edition_variant}</span>
          </DataPlate>
        </PlateGrid>
        {item.description === undefined ? (
          <p className="t-body-m t-secondary dossier-empty">
            No approved description has been published for this release yet.
          </p>
        ) : (
          <Quoted label="Approved release description" text={item.description} />
        )}
      </section>

      <section aria-labelledby="dossier-copy" className="dossier-region dossier-region--copy">
        <h2 className="t-display-xs" id="dossier-copy">
          This exact copy
        </h2>
        <p className="t-body-s t-secondary dossier-scope">
          One of one. Everything below describes the single object you would receive.
        </p>
        <PlateGrid>
          <DataPlate label="SKU">
            <Identifier label="SKU" size="m" value={item.sku} />
          </DataPlate>
          <DataPlate label="Graded by">
            {item.grading_company === undefined ? (
              <NotPublished what="this copy is not encapsulated" />
            ) : (
              <span className="t-body-m">{item.grading_company}</span>
            )}
          </DataPlate>
          <DataPlate label="Grade">
            {item.grade === undefined ? (
              <NotPublished what="no grade on this copy" />
            ) : (
              <span className="t-id t-id--m">{item.grade}</span>
            )}
          </DataPlate>
          <DataPlate
            label="Certification number"
            note={
              item.cert_number === undefined && item.grading_company !== undefined
                ? "Ask about this item and we will send the certification number so you can check it against the grader's own lookup before you buy."
                : undefined
            }
          >
            {item.cert_number === undefined ? (
              <NotPublished what="the certification number is not published in this catalogue" />
            ) : (
              <Identifier label="Certification number" size="m" value={item.cert_number} />
            )}
          </DataPlate>
          <DataPlate label="Price">
            <Money minor={item.price_minor} />
          </DataPlate>
          <DataPlate label="Where you can buy it" note={availability.detail}>
            <StatusChip label={availability.label} tone={availability.tone} />
          </DataPlate>
        </PlateGrid>
        <Quoted label="Slab condition, as inspected" text={item.condition} />
        <p className="t-body-s t-secondary">
          {item.public_derivatives.length === 0
            ? "No approved photographs have been published for this copy yet."
            : `${String(item.public_derivatives.length)} approved photographs of this exact object, not stock art.`}
        </p>
      </section>
    </div>
  );
}

/**
 * The exact-copy identity, reduced to what must never be ambiguous. Rendered
 * identically wherever an item appears after the item page, so the object never
 * silently becomes "a Super Mario Bros. 3" on the way to the payment screen.
 */
export function ItemIdentity({
  sku,
  title,
  gradingCompany,
  grade,
  certNumber
}: {
  sku: string;
  title: string;
  gradingCompany?: string | undefined;
  grade?: string | undefined;
  certNumber?: string | undefined;
}): ReactNode {
  return (
    <div className="identity">
      <p className="identity-title t-body-m">{title}</p>
      <p className="identity-ids">
        <span className="t-label">SKU</span> <Identifier label="SKU" value={sku} />
        {gradingCompany === undefined || grade === undefined ? null : (
          <>
            {" "}
            <span className="t-label">Grade</span>{" "}
            <span className="t-id">
              {gradingCompany} {grade}
            </span>
          </>
        )}
        {certNumber === undefined ? null : (
          <>
            {" "}
            <span className="t-label">Cert</span>{" "}
            <Identifier label="Certification number" value={certNumber} />
          </>
        )}
      </p>
    </div>
  );
}
